/**
 * Custom useLocation hook for KeepAlive compatibility
 *
 * React Router's useLocation doesn't work correctly in KeepAliveOutlet because
 * hidden tabs still see the global browser URL (the active tab's URL).
 *
 * This hook provides a compatibility layer that:
 * 1. First tries to get location from the TabLocationContext (set per-tab by KeepAliveOutlet)
 * 2. Falls back to react-router-dom's useLocation
 *
 * Pattern mirrors @/hooks/useParams + RouteParamsContext.
 */

import { useTabLocation, type TabLocation } from '@/context/TabLocationContext';
import { useLocation as useReactRouterLocation } from 'react-router-dom';

/**
 * Get the current location — works correctly inside KeepAliveOutlet tabs.
 * Returns the tab's own location rather than the global browser URL.
 */
export function useLocation(): TabLocation {
  const tabLocation = useTabLocation();
  const routerLocation = useReactRouterLocation();

  if (tabLocation) {
    return tabLocation;
  }

  return routerLocation;
}

export default useLocation;
